import { FormGroup, Label, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { useFormikContext } from "formik";
import { isEmpty, isNil } from "lodash";
import React from "react";
import { CatalogItemRestrictionScope, LineItemCode, LineItemTemplate, QuantitySource } from "types";
import { canMatchGuestCount, getRestrictionValue, LABEL_BY_QUANTITY_SOURCE } from "./AddOnFormUtils";
import { AddOnEditorFormState } from "./AddOnTypes";
import { useCollection } from "@/hooks/useCollection";

export const AddOnPurchaseQuantityFields = () => {
    const { values, setFieldValue } = useFormikContext<AddOnEditorFormState>();
    const isMatchAvailable = canMatchGuestCount(values);
    // The API sets the subtotal code itself, so this query only picks the label. Only sellers who sell equipment as a
    // purchasable unit have the primary unit subtotal.
    const { data: primaryUnitTemplates } = useCollection<LineItemTemplate>(
        ["lineItemTemplates"],
        { code: LineItemCode.PRIMARY_UNIT },
        { enabled: isMatchAvailable },
    );
    const quantitySource: QuantitySource = isEmpty(primaryUnitTemplates)
        ? LineItemCode.DEMOGRAPHIC
        : LineItemCode.PRIMARY_UNIT;

    if (!isMatchAvailable) {
        return null;
    }

    const handleMatchChange = (isMatching: boolean) => {
        setFieldValue("quantitySource", isMatching ? quantitySource : null);
    };

    const quantitySourceLabel = LABEL_BY_QUANTITY_SOURCE[quantitySource];
    const hasEventRestriction = !isNil(getRestrictionValue(values.restrictions, CatalogItemRestrictionScope.EVENT));

    return (
        <FormGroup>
            <Label className="mb-4">Purchase Quantity</Label>
            <Tooltip
                className="inline-flex"
                content="Can be enabled only when no limits are set for the add-on"
                disabled={!hasEventRestriction}
            >
                <Switch.Group>
                    <Switch
                        isChecked={!isNil(values.quantitySource) && !hasEventRestriction}
                        disabled={hasEventRestriction}
                        data-testid="addon-matches-guest-count"
                        onChange={handleMatchChange}
                    />
                    <Switch.Label direction="right" className={clsx(hasEventRestriction && "!cursor-not-allowed")}>
                        Automatically match add-on quantity to total {quantitySourceLabel} count
                    </Switch.Label>
                </Switch.Group>
            </Tooltip>
        </FormGroup>
    );
};
